import React, { useState } from "react";
import { fetchAnniversaryPortrait } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Shown on the Dashboard when the journaler crosses their one-year anniversary.
 * Reveals a Claude-generated poetic portrait of their year.
 */
export default function AnniversaryPortraitCard({ anniversary }) {
  const [portrait, setPortrait] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(false);

  if (!anniversary?.eligible) return null;

  const reveal = async () => {
    setLoading(true);
    try {
      const r = await fetchAnniversaryPortrait();
      setPortrait(r.portrait);
      setOpened(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not open the portrait right now.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mb-12 border border-terracotta/50 bg-gradient-to-br from-terracotta/10 to-transparent p-6 md:p-8 animate-fade-up"
      data-testid="anniversary-portrait-card"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="h-10 w-10 border border-terracotta text-terracotta flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
            One year with the workbook
          </p>
          <h3 className="font-serif text-2xl md:text-3xl text-bone-100 leading-snug">
            {opened ? "A portrait of your year" : "Your anniversary portrait is ready."}
          </h3>
        </div>
      </div>

      {!opened ? (
        <div className="pl-14">
          <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6 max-w-xl">
            The workbook has quietly polished a mirror from everything you have spoken this year.
            When you're ready, open it — a single luminous paragraph, written just for you.
          </p>
          <button
            onClick={reveal}
            disabled={loading}
            data-testid="anniversary-reveal-btn"
            className="inline-flex items-center gap-2 border border-terracotta bg-terracotta hover:bg-terracotta-deep text-bone-100 px-6 py-3 font-sans text-xs uppercase tracking-[0.24em] transition-colors"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Reveal the portrait
          </button>
        </div>
      ) : (
        <div className="pl-14 animate-fade-up">
          <p className="font-serif text-lg text-bone-100 leading-relaxed italic" data-testid="anniversary-portrait-text">
            {portrait}
          </p>
        </div>
      )}
    </div>
  );
}
